import { createClient } from '@supabase/supabase-js';

/**
 * CONFIGURACIÓN DE SUPABASE
 * 
 * Este archivo configura la conexión con Supabase y proporciona utilidades
 * para verificar si la configuración es válida.
 * 
 * PARA DESARROLLADORES JUNIOR:
 * 1. Supabase es una alternativa a Firebase que proporciona base de datos PostgreSQL
 * 2. Necesitas dos variables de entorno: URL del proyecto y clave pública (anon key)
 * 3. La clave anon es segura para usar en el frontend
 */

// Obtener variables de entorno
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Debug: Mostrar información de variables de entorno (solo en desarrollo)
if (import.meta.env.DEV) {
  console.log('🔍 DEBUG - Variables de entorno:');
  console.log('VITE_SUPABASE_URL:', supabaseUrl ? `${supabaseUrl.substring(0, 30)}...` : 'NO DEFINIDA');
  console.log('VITE_SUPABASE_ANON_KEY:', supabaseAnonKey ? `${supabaseAnonKey.substring(0, 20)}...` : 'NO DEFINIDA');
  console.log('Todas las variables VITE_:', Object.keys(import.meta.env).filter(key => key.startsWith('VITE_')));
}

/**
 * Verifica si las credenciales de Supabase están configuradas correctamente
 * 
 * @returns true si las credenciales son válidas, false si son valores demo
 */
const hasValidConfig = () => {
  const isValid = supabaseUrl && 
    supabaseAnonKey && 
    supabaseUrl.trim() !== '' &&
    supabaseAnonKey.trim() !== '' &&
    supabaseUrl !== 'https://demo.supabase.co' && 
    supabaseAnonKey !== 'demo-key' &&
    supabaseUrl.includes('supabase.co');
  
  if (import.meta.env.DEV) {
    console.log('🔍 Validación de configuración Supabase:', {
      hasUrl: !!supabaseUrl,
      hasKey: !!supabaseAnonKey,
      urlValid: supabaseUrl?.includes('supabase.co'),
      isValid
    });
  }
  
  return isValid;
};

/**
 * Validación de configuración con mensajes informativos
 */
if (!supabaseUrl || !supabaseAnonKey) {
  console.warn(`
    ⚠️  SUPABASE NO CONFIGURADO
    
    Para conectar con Supabase, crea un archivo .env en la raíz del proyecto:
    
    VITE_SUPABASE_URL=https://tu-proyecto.supabase.co
    VITE_SUPABASE_ANON_KEY=tu-clave-publica-aqui
    
    Mientras tanto, la app funcionará con datos locales.
  `);
} else if (!hasValidConfig()) {
  console.warn(`
    ⚠️  USANDO VALORES DEMO DE SUPABASE
    
    Detectamos valores de demostración. Para usar tu proyecto real:
    1. Ve a https://supabase.com/dashboard
    2. Crea un nuevo proyecto
    3. Copia la URL y anon key a tu archivo .env
  `);
}

/**
 * Cliente de Supabase
 * 
 * IMPORTANTE: Siempre se crea el cliente, incluso con valores demo,
 * para evitar errores en tiempo de ejecución.
 */
export const supabase = createClient(
  supabaseUrl || 'https://demo.supabase.co',
  supabaseAnonKey || 'demo-key'
);

/**
 * Verifica si Supabase está configurado correctamente
 * 
 * USO: Úsala antes de hacer operaciones de base de datos
 * 
 * @returns boolean - true si está configurado, false si no
 */
export const isSupabaseConfigured = (): boolean => {
  return hasValidConfig();
};

/**
 * Prueba la conexión con Supabase
 * 
 * PARA DESARROLLADORES JUNIOR:
 * Esta función intenta hacer una consulta simple para verificar que:
 * 1. Las credenciales son correctas
 * 2. La tabla 'users' existe
 * 3. Tenemos permisos para leer
 * 
 * @returns Promise<boolean> - true si la conexión es exitosa
 */
export const testSupabaseConnection = async (): Promise<boolean> => {
  if (!hasValidConfig()) {
    console.info('🔄 Supabase no configurado - usando modo local');
    return false;
  }
  
  try {
    console.log('🔍 Probando conexión con Supabase...');
    console.log('🌐 URL:', supabaseUrl);
    console.log('🔑 Key (primeros 20 caracteres):', supabaseAnonKey?.substring(0, 20) + '...');
    
    // Intenta una consulta simple para verificar la conexión
    const { data, error } = await supabase
      .from('users')
      .select('id')
      .limit(1);
    
    if (error) {
      console.error('❌ Error de conexión con Supabase:', {
        message: error.message,
        code: error.code,
        details: error.details,
        hint: error.hint
      });
      console.info('💡 Posibles soluciones:');
      console.info('   1. Verifica que la tabla "users" exista');
      console.info('   2. Revisa las políticas RLS (Row Level Security)');
      console.info('   3. Confirma que la anon key tenga permisos');
      return false;
    }
    
    console.log('✅ Conexión con Supabase exitosa!');
    console.log('📊 Resultado de prueba:', { recordsFound: data?.length || 0 });
    return true;
  } catch (error) {
    console.error('❌ Error inesperado al conectar con Supabase:', {
      error: error instanceof Error ? error.message : error,
      stack: error instanceof Error ? error.stack : undefined
    });
    console.info('🔄 La app continuará funcionando con datos locales');
    return false;
  }
};

/**
 * TIPOS DE DATOS PARA SUPABASE
 * 
 * Estos tipos ayudan a mantener consistencia entre el frontend y la base de datos
 */
export interface SupabaseUser {
  id: string;
  name: string;
  age: number | null;
  location: string | null;
  images: string[] | null;
  bio: string | null;
  user_type: 'entrepreneur' | 'investor';
  email: string | null;
  phone: string | null;
  website: string | null;
  created_at: string;
  updated_at: string;
  
  // Campos específicos de emprendedores
  company: string | null;
  industry: string | null;
  stage: 'idea' | 'prototype' | 'mvp' | 'early-revenue' | 'scaling' | null;
  funding_needed: string | null;
  previous_experience: string | null;
  business_model: string | null;
  team_size: number | null;
  achievements: string[] | null;
  
  // Campos específicos de inversores
  investor_type: 'angel' | 'vc' | 'corporate' | 'family-office' | null;
  investment_range: string | null;
  focus_industries: string[] | null;
  investment_stage: string[] | null;
  portfolio_size: number | null;
  notable_investments: string[] | null;
  expertise: string[] | null;
}

/**
 * UTILIDADES PARA DESARROLLADORES JUNIOR
 */

/**
 * Convierte un objeto User (frontend) a formato Supabase (backend)
 * 
 * EXPLICACIÓN: El frontend usa camelCase (userName) pero PostgreSQL
 * tradicionalmente usa snake_case (user_name)
 */
export const mapUserToSupabase = (user: any): SupabaseUser => {
  const baseData = {
    id: user.id,
    name: user.name,
    age: user.age || null,
    location: user.location || null,
    images: user.images || null,
    bio: user.bio || null,
    user_type: user.userType,
    email: user.email || null,
    phone: user.phone || null,
    website: user.website || null,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  };

  if (user.userType === 'entrepreneur') {
    return {
      ...baseData,
      // Campos de emprendedor
      company: user.company || null,
      industry: user.industry || null,
      stage: user.stage || null,
      funding_needed: user.fundingNeeded || null,
      previous_experience: user.previousExperience || null,
      business_model: user.businessModel || null,
      team_size: user.teamSize || null,
      achievements: user.achievements || null,
      // Campos de inversor en null
      investor_type: null,
      investment_range: null,
      focus_industries: null,
      investment_stage: null,
      portfolio_size: null,
      notable_investments: null,
      expertise: null
    };
  } else {
    return {
      ...baseData,
      // Campos de inversor
      investor_type: user.investorType || null,
      investment_range: user.investmentRange || null,
      focus_industries: user.focusIndustries || null,
      investment_stage: user.investmentStage || null,
      portfolio_size: user.portfolioSize || null,
      notable_investments: user.notableInvestments || null,
      expertise: user.expertise || null,
      // Campos de emprendedor en null
      company: null,
      industry: null,
      stage: null,
      funding_needed: null,
      previous_experience: null,
      business_model: null,
      team_size: null,
      achievements: null
    };
  }
};

/**
 * Convierte datos de Supabase a formato del frontend
 */
export const mapSupabaseToUser = (supabaseUser: SupabaseUser): User => {
  // Asegurar que tenemos imágenes por defecto si no hay ninguna
  const defaultImages = [
    'https://images.pexels.com/photos/1043471/pexels-photo-1043471.jpeg?auto=compress&cs=tinysrgb&w=400&h=600&fit=crop',
    'https://images.pexels.com/photos/1181690/pexels-photo-1181690.jpeg?auto=compress&cs=tinysrgb&w=400&h=600&fit=crop',
    'https://images.pexels.com/photos/1181686/pexels-photo-1181686.jpeg?auto=compress&cs=tinysrgb&w=400&h=600&fit=crop'
  ];

  const baseUser = {
    id: supabaseUser.id,
    name: supabaseUser.name,
    age: supabaseUser.age || 25,
    location: supabaseUser.location || 'Madrid, España',
    images: supabaseUser.images && supabaseUser.images.length > 0 ? supabaseUser.images : defaultImages,
    bio: supabaseUser.bio || 'Profesional apasionado por la innovación y el crecimiento empresarial.',
    userType: supabaseUser.user_type,
    email: supabaseUser.email || undefined,
    phone: supabaseUser.phone || undefined,
    website: supabaseUser.website || undefined
  };

  if (supabaseUser.user_type === 'entrepreneur') {
    return {
      ...baseUser,
      userType: 'entrepreneur' as const,
      company: supabaseUser.company || 'Startup Innovadora',
      industry: supabaseUser.industry || 'Tecnología',
      stage: supabaseUser.stage || 'idea',
      fundingNeeded: supabaseUser.funding_needed || '$100K - $500K',
      previousExperience: supabaseUser.previous_experience || 'Experiencia en desarrollo de productos',
      businessModel: supabaseUser.business_model || 'B2B SaaS',
      teamSize: supabaseUser.team_size || 1,
      achievements: supabaseUser.achievements || []
    } as Entrepreneur;
  } else {
    return {
      ...baseUser,
      userType: 'investor' as const,
      investorType: supabaseUser.investor_type || 'angel',
      investmentRange: supabaseUser.investment_range || '$50K - $250K',
      focusIndustries: supabaseUser.focus_industries || ['Tecnología', 'SaaS'],
      investmentStage: supabaseUser.investment_stage || ['Seed', 'Series A'],
      portfolioSize: supabaseUser.portfolio_size || 0,
      notableInvestments: supabaseUser.notable_investments || [],
      expertise: supabaseUser.expertise || []
    } as Investor;
  }
};